import { ArrowUpRight, HeartPulse, Users, Home, Sparkles, Car, HandHeart } from "lucide-react";

const SERVICES = [
  {
    key: "daily-care",
    title: "Daily Personal Activities & Care",
    description: "Compassionate, dignified help with morning routines, personal hygiene, medication prompts and everyday wellbeing.",
    image: "https://images.pexels.com/photos/8127504/pexels-photo-8127504.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: HeartPulse,
  },
  {
    key: "community",
    title: "Community Participation & Social Support",
    description: "From local meet‑ups to capacity‑building groups — we walk alongside you, building confidence and connection.",
    image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=940&h=650&fit=crop",
    icon: Users,
  },
  {
    key: "sil",
    title: "Supported Independent Living (SIL)",
    description: "Warm shared homes and tailored 1:1 living support so you can live independently — your way.",
    image: "https://images.pexels.com/photos/12673629/pexels-photo-12673629.jpeg?w=940&h=650&fit=crop",
    icon: Home,
  },
  {
    key: "household",
    title: "Household Tasks & Domestic Help",
    description: "Cleaning, laundry, meal preparation and gentle home maintenance — done with care, never rushed.",
    image: "https://images.pexels.com/photos/6073640/pexels-photo-6073640.jpeg?w=940&h=650&fit=crop",
    icon: Sparkles,
  },
  {
    key: "transport",
    title: "Transport & Travel Assistance",
    description: "Safe, reliable transport to appointments, work, study and the activities that bring you joy.",
    image: "https://images.unsplash.com/photo-1773239674750-3227881cff01?w=940&h=650&fit=crop",
    icon: Car,
  },
  {
    key: "coordination",
    title: "Support Coordination",
    description: "Navigate your plan with clarity. We connect you with the right providers and make the system work for you.",
    image: "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=940&h=650&fit=crop",
    icon: HandHeart,
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-28 bg-brand-bone"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.22em] text-brand-terracotta font-semibold">
            What we offer
          </span>
          <h2 className="mt-3 font-heading font-semibold tracking-tight text-brand-forest text-3xl sm:text-4xl lg:text-5xl">
            NDIS supports, shaped around <span className="italic font-medium">you</span>.
          </h2>
          <p className="mt-5 text-base lg:text-lg text-brand-muted leading-relaxed">
            Every plan is different — and so is every person. Our team builds your supports together with you,
            your family and your support network.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <article
                key={s.key}
                data-testid={`service-card-${s.key}`}
                className="group bg-white rounded-2xl border border-brand-sand overflow-hidden shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md flex flex-col"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute top-4 left-4 h-10 w-10 rounded-xl bg-white/95 grid place-items-center text-brand-forest shadow-sm">
                    <Icon className="h-5 w-5" />
                  </div>
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <h3 className="font-heading text-xl font-semibold text-brand-ink leading-snug">{s.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-muted flex-1">{s.description}</p>
                  <a
                    href="#contact"
                    data-testid={`service-learn-more-${s.key}`}
                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-forest hover:text-brand-terracotta transition-colors"
                  >
                    Learn more
                    <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
